import React, { useState, useEffect } from "react"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Divider from "@mui/material/Divider"
import Avatar from "@mui/material/Avatar"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Paper from "@mui/material/Paper"

import CloseIcon from "@mui/icons-material/Close"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import BusinessIcon from "@mui/icons-material/Business"
import StorefrontIcon from "@mui/icons-material/Storefront"
import PersonIcon from "@mui/icons-material/Person"
import PropaneTankIcon from '@mui/icons-material/PropaneTank'

import DetailItem from "./DetailItem"
import EstadoChip from "./EstadoChip"
import { formatFecha } from "../utils/fecha"
import { getItemsRemito } from "../api"

export default function DialogDetalleRemito({ open, remito, onClose }) {
    const [items, setItems] = useState([])

    useEffect(() => {
        if (remito?.id_remito) {
            getItemsRemito(remito.id_remito, setItems)
        }
    }, [remito])

    // Cálculo opcional del total de unidades agregadas
    const totalCantidad = items?.reduce((acc, item) => acc + (Number(item.cantidad) || 0), 0) || 0

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, overflow: "hidden" },
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    color: "primary.contrastText",
                    background: "linear-gradient(135deg, #093457 0%, #0f4c81 55%, #3f74a8 100%)",
                }}
            >
                <IconButton
                    onClick={onClose}
                    aria-label="Cerrar"
                    sx={{ position: "absolute", top: 12, right: 12, color: "rgba(255,255,255,0.85)" }}
                >
                    <CloseIcon />
                </IconButton>

                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        variant="rounded"
                        sx={{
                            bgcolor: "rgba(255,255,255,0.15)",
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                        }}
                    >
                        <ReceiptLongIcon fontSize="large" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: "0.12em" }}>
                            Detalle del Remito
                        </Typography>
                        <Typography variant="h5" sx={{ lineHeight: 1.1 }}>
                            N° {remito?.nro}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <EstadoChip estado={remito?.estado} />
                        </Box>
                    </Box>
                </Stack>
            </Box>

            <DialogContent sx={{ px: { xs: 3, sm: 4 }, py: 3 }}>
                {/* Sección de datos generales del remito */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <DetailItem
                            icon={<CalendarMonthIcon fontSize="small" />}
                            label="Fecha"
                            value={formatFecha(remito?.fecha)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DetailItem
                            icon={<BusinessIcon fontSize="small" />}
                            label="Empresa"
                            value={remito?.empresa}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DetailItem
                            icon={<StorefrontIcon fontSize="small" />}
                            label="Sucursal"
                            value={remito?.sucursal}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DetailItem
                            icon={<PersonIcon fontSize="small" />}
                            label="Cliente"
                            value={remito?.cliente}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                {/* Sección de Ítems / Productos */}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 700, letterSpacing: "0.05em" }}>
                    ITEMS DEL REMITO ({items?.length || 0})
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        borderColor: "rgba(232, 131, 58, 0.25)",
                        bgcolor: "rgba(232, 131, 58, 0.02)",
                    }}
                >
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: "rgba(232, 131, 58, 0.08)" }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>PRODUCTO / SERVICIO</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: "text.secondary" }}>U. MEDIDA</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: "text.secondary" }}>CANTIDAD</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items && items.length > 0 ? (
                                    items.map((item, index) => (
                                        <TableRow key={item.id_item || index} hover>
                                            <TableCell sx={{ width: 40 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {index + 1}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Avatar
                                                        variant="rounded"
                                                        sx={{
                                                            bgcolor: "rgba(232, 131, 58, 0.15)",
                                                            color: "#e8833a",
                                                            width: 32,
                                                            height: 32,
                                                        }}
                                                    >
                                                        <PropaneTankIcon fontSize="small" />
                                                    </Avatar>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {item.producto}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.nombre_um}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#c26720" }}>
                                                    {item.cantidad}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Sin ítems registrados.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Resumen al pie del listado */}
                    {items && items.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 2,
                                borderTop: "1px solid",
                                borderColor: "rgba(232, 131, 58, 0.15)",
                                bgcolor: "rgba(232, 131, 58, 0.05)",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                TOTAL UNIDADES:
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#c26720" }}>
                                {totalCantidad}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: { xs: 3, sm: 4 }, py: 2 }}>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose} variant="contained" color="primary">
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
